// src/components/SiteInformationViewer.jsx

import React, { useEffect, useState } from "react";
import { Typography, Box, Grid } from "@mui/material";
import DocumentButton from "./DocumentButton";
import axios from "axios";

function SiteInformationViewer({ siteId, responses, handleChange }) {
  const API_BASE_URL = "https://formbuilderapi.getforsite.com";
  // 1) Start from any pre-loaded siteInformation in the responseData
  const preloaded = Array.isArray(responses.siteInformation)
    ? responses.siteInformation
    : [];

  const [documents, setDocuments] = useState(preloaded);

  useEffect(() => {
    // 2) If we already have preloaded docs, do nothing
    if (preloaded.length) return;

    // 3) Otherwise fetch from the API
    const fetchDocuments = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/site-information/${siteId}`,
        );
        setDocuments(data.documents);
      } catch (error) {
        console.error("Error fetching site information:", error);
        alert("An error occurred while fetching site information.");
      }
    };

    if (siteId) {
      fetchDocuments();
    }
  }, [siteId, preloaded.length]);

  // 4) Mirror back into responseData if it’s changed
  useEffect(() => {
    const current = responses.siteInformation || [];
    const same =
      current.length === documents.length &&
      current.every(
        (d, i) =>
          d.id === documents[i]?.id &&
          d.title === documents[i]?.title &&
          d.uploaded_url === documents[i]?.uploaded_url,
      );

    if (!same) {
      handleChange("siteInformation", documents);
    }
  }, [documents, handleChange, responses.siteInformation]);

  if (!siteId) return null;

  return (
    <Box marginY={2} width="100%">
      <Typography variant="h6" gutterBottom>
        Site Information Documents
      </Typography>
      {documents.length === 0 ? (
        <Typography>No site information documents available.</Typography>
      ) : (
        <Grid container spacing={2}>
          {documents.map((doc) => (
            <Grid item key={doc.id} xs={12} sm={4}>
              <DocumentButton
                doc={doc}
                onClick={() => {
                  const url = doc?.uploaded_url;
                  if (!url) return;

                  // If we're inside an iframe (kiosk), ask the parent to open it (presign + navigate)
                  const inIframe = window.self !== window.top;

                  if (inIframe) {
                    window.parent.postMessage(
                      { type: "OPEN_S3_DOC", url },
                      "*",
                    );
                    return;
                  }

                  // Normal browser usage (non-kiosk)
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default SiteInformationViewer;
